import { Button, Cell, Combobox, Row, Tag, Tooltip } from "@hellocarbo/lasagne";
import { Check } from "lucide-react";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher, useParams } from "react-router";
import invariant from "tiny-invariant";

import { PortalContainerContext } from "@/contexts";
import { TMatchingResponse, TReferential } from "@/types";

type MatchingRowProps = {
  matching: TMatchingResponse;
  referentials: TReferential[];
};

function MatchingRow({ matching, referentials }: MatchingRowProps) {
  const { t } = useTranslation(["importDeposit"]);
  const { container } = useContext(PortalContainerContext);
  const fetcher = useFetcher();
  const { scoreId, importId } = useParams();
  invariant(scoreId, "No scoreId found!");
  invariant(scoreId, "No scoreId found!");

  const [isValidated, setValidationState] = useState<boolean>(
    matching.status === "validated"
  );
  const [comboboxValue, setComboboxValue] = useState<string>(
    matching.selected_dr_unique_code ?? matching.suggested_dr_unique_code
  );
  const referentialsFormatted = [{ items: referentials }];

  const submitUpdate = (status: string, value: string) => {
    fetcher.submit(
      {
        id: matching.id,
        scoreId: scoreId || "",
        importId: importId || "",
        status,
        selected_dr_unique_code: value,
      },
      { method: "PATCH" }
    );
  };
  const handleComboboxChange = (value: string) => {
    setValidationState(false);
    setComboboxValue(value);
    submitUpdate("pending", comboboxValue);
  };

  const handleValidationClick = () => {
    // If the matching was already validated, we do nothing
    if (!isValidated) {
      setValidationState(true);
      submitUpdate("validated", comboboxValue);
    }
  };

  return (
    <Row>
      {matching.context.element_names.length === 1 ? (
        <Cell>{t(matching.context.element_names[0])}</Cell>
      ) : (
        <Cell>
          {t(`importDeposit:importMatchingTable.affectedProductsNumber`, {
            x: matching.context.element_names.length,
          })}
        </Cell>
      )}
      <Cell>{t(matching.name)}</Cell>

      <Cell>
        <div className="max-w-[75%]">
          <Combobox
            groups={referentialsFormatted}
            defaultValue={comboboxValue}
            size="medium"
            onChange={handleComboboxChange}
          />
        </div>
      </Cell>

      <Cell>
        <Tag
          text={
            isValidated
              ? t("importDeposit:importMatchingTable.statusValidated")
              : t("importDeposit:importMatchingTable.status")
          }
          intent={isValidated ? "positive" : "warning"}
        />
      </Cell>
      <Cell className="text-right">
        <Tooltip
          portalContainer={container as Element}
          side="left"
          content={t(`importDeposit:importMatchingTable.validateTooltip`)}
          triggerAsChild
        >
          <Button
            shape="soft"
            intent="primary"
            size="small"
            Icon={Check}
            onClick={handleValidationClick}
          />
        </Tooltip>
      </Cell>
    </Row>
  );
}

export default MatchingRow;
